/**
 * The positive skill chips of a PAST session's transcript (the history's
 * session detail). The live thread decides its chips from the live events
 * (`selectSkillChips`: verdicts, `display`, prior turns), none of which is
 * stored; the history rebuilds them from the session summary's
 * `skillCredits` instead — the same DB source as the debrief's "Lo que
 * lograste hoy", so the history and the debrief always agree.
 *
 * What that source already settled server-side (`buildSessionSkillCredits`):
 * a credit contradicted by a correction of its own turn is left out, a nail
 * is withdrawn when the session also has an error of it, and each skill comes
 * once, on the turn of the credit that names it (the nail, else the first
 * counted one) — so "once per skill per session" holds by construction.
 *
 * The live rules applied on top, per credit:
 *   - no chip for the lexicon's vocabulary (live it is `silent`) nor for a
 *     session-end credit (no turn: it never had a bubble);
 *   - `completed` when the session completed a celebrated skill;
 *   - `nailed` ("lo clavaste") when it nailed a radar focus, once per focus;
 *   - `grammar` for a counted rep of a celebrated grammar skill that was not
 *     complete before — "label · n/3" with the reps AS OF that credit.
 * One chip per turn, by the same priority as live; ties go to the first.
 *
 * The clean-sentence ✓ (D10) is not a chip: it comes from the turn's persisted
 * corrections verdict (`selectTranscriptCleanTicks`), and a chip here wins
 * over it.
 */
import { isSessionEndCredit, isSilentVocabularyCredit } from '@orborix/shared-types';
import type { SessionSummarySkillCredit } from '../../lib/sessionSummaryApi.js';
import type { UiLocale } from '../conversation/coachCatalog.js';
import { isShortPhrase, type SkillChipKind, type SkillChipModel } from '../conversation/skillChips.js';

const PRIORITY: Record<SkillChipKind, number> = { completed: 0, nailed: 1, grammar: 2 };

function chipFor(
  credit: SessionSummarySkillCredit,
  focusFree: boolean,
  locale: UiLocale,
): SkillChipModel | null {
  const label = credit.label[locale];
  // Reps right after the credit that names the skill: the session's counted
  // credits come in order, and that one is its first counted credit.
  const repsAtCredit = Math.max(credit.reps - credit.gained + 1, 1);
  const base = {
    skillId: credit.skillId,
    label,
    evidence: credit.evidence,
    reps: Math.min(repsAtCredit, credit.repsToComplete),
    repsToComplete: credit.repsToComplete,
    headline: label,
    quoted: false,
    focusLabel: null,
  };
  if (credit.completedNow && credit.celebrate) return { ...base, kind: 'completed' };
  if (credit.nailedFocus && focusFree) {
    const quoted = isShortPhrase(credit.evidence);
    return {
      ...base,
      kind: 'nailed',
      headline: quoted ? credit.evidence.trim() : label,
      quoted,
      focusLabel: credit.nailedFocus.label,
    };
  }
  if (credit.area === 'grammar' && credit.celebrate && credit.gained > 0 && !credit.completedBefore) {
    return { ...base, kind: 'grammar' };
  }
  return null;
}

/** Chip per turn index of the session. Turns without a chip are absent. */
export function selectTranscriptSkillChips(
  credits: ReadonlyArray<SessionSummarySkillCredit>,
  locale: UiLocale = 'es',
): ReadonlyMap<number, SkillChipModel> {
  const out = new Map<number, SkillChipModel>();
  /** Foci already praised: "lo clavaste" is once per focus per session. */
  const nailedFoci = new Set<string>();
  for (const credit of credits) {
    if (credit.turnIndex === null) continue;
    if (isSilentVocabularyCredit(credit) || isSessionEndCredit(credit)) continue;
    const focusId = credit.nailedFocus?.focusId ?? null;
    const chip = chipFor(credit, focusId === null || !nailedFoci.has(focusId), locale);
    if (!chip) continue;
    const current = out.get(credit.turnIndex);
    if (current && PRIORITY[current.kind] <= PRIORITY[chip.kind]) continue;
    out.set(credit.turnIndex, chip);
    if (focusId !== null && chip.kind !== 'grammar') nailedFoci.add(focusId);
  }
  return out;
}
